import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PatreonNav from "../components/PatreonNav";
import PatreonFooter from "../components/PatreonFooter";

interface Resource {
  category: string;
  title: string;
  description: string;
  image: string;
  link: string;
}

const ResourcesPage = () => {
  const resources: Resource[] = [
    {
      category: "Getting Started",
      title: "Complete Creator's Guide",
      description: "Everything you need to know to launch your creator page successfully",
      image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=600&fit=crop",
      link: "/resources/creators-guide"
    },
    {
      category: "Growth",
      title: "Building Your First 100 Patrons",
      description: "Proven strategies to grow your membership from zero to 100",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop",
      link: "/resources/first-100-patrons"
    },
    {
      category: "Content Strategy",
      title: "Creating Irresistible Membership Tiers",
      description: "Design tiers that your audience can't resist joining",
      image: "https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?w=800&h=600&fit=crop",
      link: "/resources/membership-tiers"
    },
    {
      category: "Marketing",
      title: "Social Media Strategy for Creators",
      description: "Turn your followers into paying patrons with these tactics",
      image: "https://images.unsplash.com/photo-1611926653458-09294b3142bf?w=800&h=600&fit=crop",
      link: "/resources/social-media"
    },
    {
      category: "Community",
      title: "Engaging Your Community",
      description: "Keep your patrons happy and reduce churn with engagement tactics",
      image: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&h=600&fit=crop",
      link: "/resources/community-engagement"
    },
    {
      category: "Success Stories",
      title: "How KAMAUU Built a Thriving Music Career",
      description: "From independent artist to sustainable income through Patreon",
      image: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=800&h=600&fit=crop",
      link: "/resources/kamauu-story"
    },
    {
      category: "Business",
      title: "Pricing Your Membership Correctly",
      description: "Find the sweet spot that maximizes revenue without losing patrons",
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=600&fit=crop",
      link: "/resources/pricing-strategy"
    },
    {
      category: "Tools",
      title: "Essential Tools for Content Creators",
      description: "Software and hardware recommendations from successful creators",
      image: "https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=800&h=600&fit=crop",
      link: "/resources/creator-tools"
    },
    {
      category: "Analytics",
      title: "Understanding Your Creator Dashboard",
      description: "Make data-driven decisions with your Patreon analytics",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop",
      link: "/resources/analytics-guide"
    }
  ];

  const categories = ["All", "Getting Started", "Growth", "Content Strategy", "Marketing", "Community", "Business"];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Resources | Patreon</title>
        <meta name="description" content="Guides, stories, and resources to help creators succeed on Patreon." />
      </Helmet>

      <PatreonNav />
      
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1
            className="text-6xl md:text-7xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Learn, grow, and <span style={{ color: "var(--patreon-coral)" }}>thrive</span>
          </motion.h1>
          
          <motion.p
            className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Expert guides, success stories, and practical advice from creators who have been there.
          </motion.p>

          <motion.div
            className="flex flex-wrap justify-center gap-3 mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {categories.map((category, index) => (
              <button
                key={category}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  index === 0 ? "text-white" : "bg-gray-100 hover:bg-gray-200"
                }`}
                style={index === 0 ? { backgroundColor: "var(--patreon-coral)" } : undefined}
              >
                {category}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="pb-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {resources.map((resource, index) => (
              <motion.div
                key={index}
                className="group cursor-pointer"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.05 * index }}
              >
                <Link to={resource.link} className="block">
                  <div className="relative overflow-hidden rounded-3xl mb-4">
                    <img
                      src={resource.image}
                      alt={resource.title}
                      className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div
                      className="absolute top-4 left-4 px-4 py-1 rounded-full text-sm font-semibold text-white"
                      style={{ backgroundColor: "var(--patreon-coral)" }}
                    >
                      {resource.category}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold mb-2 group-hover:text-[var(--patreon-coral)] transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{resource.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 bg-black text-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Get creator tips delivered weekly
            </h2>
            <p className="text-xl text-gray-400 mb-8">
              Join 50,000+ creators getting insider strategies, success stories, and platform updates.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-full text-black"
              />
              <button
                type="submit"
                className="px-8 py-4 rounded-full font-semibold hover:opacity-90 transition-all"
                style={{ backgroundColor: "var(--patreon-coral)", color: "white" }}
              >
                Subscribe
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Need more help?
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: "📚",
                title: "Help Center",
                description: "Comprehensive documentation and FAQs",
                link: "/help",
                linkText: "Browse articles"
              },
              {
                icon: "💬",
                title: "Community Forum",
                description: "Connect with other creators and share tips",
                link: "/community",
                linkText: "Join discussion"
              },
              {
                icon: "��",
                title: "Creator Support",
                description: "Get personalized help from our team",
                link: "/support",
                linkText: "Contact support"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-gray-50 rounded-3xl p-8 text-center hover:shadow-xl transition-shadow"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index }}
              >
                <div className="text-6xl mb-4">{item.icon}</div>
                <h3 className="text-2xl font-bold mb-3">{item.title}</h3>
                <p className="text-gray-600 mb-6">{item.description}</p>
                <Link
                  to={item.link}
                  className="inline-block font-semibold hover:opacity-80 transition-opacity"
                  style={{ color: "var(--patreon-coral)" }}
                >
                  {item.linkText} →
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <PatreonFooter />
    </div>
  );
};

export default ResourcesPage;
